import { partition } from "lodash";

export default class LocalStorageCache<T> {
  name: string;
  expiry: number; // Time from set to expire in ms

  constructor(name: string, expiry: number) {
    this.name = name;
    this.expiry = expiry;
    this.clearExpired();
  }

  // key is the key to be fetched
  // fetchData is the backup plan if not in cache
  async get(key: string, fetchData: (key: string) => T): Promise<T> {
    //type the fetchData
    if (this.keyExistsActive(key)) {
      return this.getCachedObject(key).value;
    } else {
      localStorage.removeItem(this.cacheKey(key));
      let data = await fetchData(key);
      if (!!data) this.set(key, data as unknown as object);
      return data as unknown as T;
    }
  }

  // keys are the keys to be fetched
  // fetchData is the backup plan for the IDs not found in cache, takes an array of keys and returns an object of key => data
  async getMany(
    keys: string[],
    fetchData: (keys: string[]) => Promise<{ string: T }>,
  ): Promise<T[]> {
    //type the fetchData
    let [cachedKeys, uncachedKeys] = partition(keys, key => this.keyExistsActive(key));
    let results = cachedKeys.map(key => this.getCachedObject(key).value);
    if (uncachedKeys.length > 0) {
      let uncachedResults = await fetchData(uncachedKeys);

      let uncachedResultsArray = uncachedKeys
        .map(key => {
          if (uncachedResults[key]) return this.set(key, uncachedResults[key]);
        })
        .filter(res => !!res);

      results = results.concat(uncachedResultsArray);
    }
    return results;
  }

  remove(key) {
    localStorage.removeItem(this.cacheKey(key));
  }

  private getCachedObject(key: string) {
    return JSON.parse(localStorage.getItem(this.cacheKey(key)));
  }

  private getAllKeys() {
    return Object.keys(localStorage)
      .filter(key => key.startsWith(`${this.name}|`))
      .map(key => key.substring(this.name.length + 1));
  }

  private clearExpired() {
    this.getAllKeys().forEach(key => {
      if (!this.keyExistsActive(key, true)) this.remove(key);
    });
  }

  private set(key: string, value: object) {
    let fullObj = {
      value: value,
      retailerId: window["$rootScope"].currentRetailerId,
      expiresAt: Date.now() + this.expiry,
    };
    try {
      localStorage.setItem(this.cacheKey(key), JSON.stringify(fullObj));
    } catch(e) {
      this.getAllKeys().forEach(key => this.remove(key));
    }
    return value;
  }

  private cacheKey(key: string): string {
    return `${this.name}|${key}`;
  }

  private keyExistsActive(key: string, skipRetailerCheck?: boolean): boolean {
    let storedItem = this.getCachedObject(key);
    let retailerIdMatches =
      skipRetailerCheck || storedItem?.retailerId == window["$rootScope"]?.currentRetailerId;
    return storedItem && storedItem.expiresAt > Date.now() && retailerIdMatches;
  }
}
